import Phaser from 'phaser';
import { monstersData } from '@dropfall/shared';
import { resolveAssetUrl } from '../../ui/assets';

/**
 * 몬스터 스프라이트(assets/atlas.config.json의 "monsters" 아틀라스).
 *
 * **왜 game 아틀라스와 분리돼 있나** — 여기 쓰는 에셋 팩은 재업로드 금지 라이센스라
 * 저장소에 올릴 수 없어서 아틀라스를 따로 뽑는다(assets/sprites/monsters/README.md).
 * 그래서 이 아틀라스는 **없을 수 있다** — 에셋을 받지 않은 팀원의 클론에서는 파일
 * 자체가 없고, 그때는 렌더러가 기존 도형 플레이스홀더로 그대로 떨어진다.
 */
export const MONSTER_ATLAS = 'monsters';

/**
 * 몬스터 타입(monsters.json 키) → 스프라이트 팩 파일명.
 *
 * 팩의 원본 파일명을 그대로 쓴다(공백·대문자 포함). 파일명을 우리 규칙으로 바꾸지
 * 않는 이유: 에셋을 다시 받으면 원래 이름으로 돌아오는데, 그때마다 손으로 고치면
 * "받는 사람마다 다르게 동작"하는 상태가 된다. 이름 변환을 사람이 아니라 이 표가
 * 담당하면 팩을 그대로 풀어 넣기만 하면 된다.
 *
 * 아틀라스 프레임 이름 규칙은 `{파일명}_{태그}_{태그내번호}`다(assets/README.md).
 */
const SPRITE_FILE: Record<string, string> = {
  demon: 'Demon_A',
  hellhound: 'Hellhound',
  blood: 'Blood Monster_A',
  eyeball: 'Eyeball Monster',
  lava_slime: 'Lava Slime',
  minotaur: 'Minotaur',
  boss_demon: 'Demon_E',
  boss_knight: 'Black Knight_A',
  boss_golem: 'Flame Golem',
  boss_dark_knight: 'Black Knight_C',
};

/**
 * 그림 높이(px). 실측값이다 — 100×100 캔버스에서 Idle/Walk 프레임의 불투명 픽셀
 * 세로 범위를 재서 넣었다. HP 바를 머리 위에 띄우는 데만 쓴다(판정과 무관).
 * 배율을 바꾸면 여기도 같이 바꿔야 한다.
 */
const SPRITE_HEIGHT: Record<string, number> = {
  demon: 22,
  hellhound: 20,
  blood: 19,
  eyeball: 17,
  lava_slime: 19,
  minotaur: 23,
  boss_demon: 25,
  boss_knight: 33,
  boss_golem: 31,
  boss_dark_knight: 31,
};

/** 그림 높이를 모를 때(표에 없는 타입) 쓰는 값. */
const DEFAULT_SPRITE_HEIGHT = 24;

/**
 * 스프라이트 원점의 세로 위치(프레임 높이 대비 비율).
 *
 * 팩의 10종 전부가 100px 캔버스의 **y=60에 발이 닿게** 그려져 있어서(실측) 타입마다
 * 따로 잴 필요가 없다. 발밑을 원점으로 잡아야 탑다운 Y-정렬이 맞는다 — 플레이어
 * 스프라이트(PLAYER_ORIGIN_Y)와 같은 규칙이다.
 */
export const MONSTER_ORIGIN_Y = 0.6;

/**
 * 렌더 배율.
 *
 * 잡몹은 팩 그림이 이미 17~30px라(플레이어 32px와 같은 대역) 확대 없이 쓴다.
 * 히트박스보다 그림이 1.5~2배 큰데, 플레이어도 같은 비율이라(스프라이트 32px vs
 * 판정 지름 20px) 일관된 감각이다.
 *
 * **보스는 다르다.** 같은 100px 캔버스에 그려져 있어도 실제 그림은 25~33px라,
 * 1:1로 그리면 잡몹과 덩치가 같아서 "보스가 왔다"는 느낌이 전혀 안 난다. 배율을
 * 올리면 monsters.json의 hitRadius와 검술 사거리도 같은 배수로 올려야 한다 —
 * 보이는 크기와 맞는 범위가 어긋나면 안 된다.
 */
const SCALE: Record<string, number> = {
  boss_demon: 2.5,
  boss_knight: 2.5,
  boss_golem: 3,
  boss_dark_knight: 3,
};
const DEFAULT_SCALE = 1;

export function monsterScale(type: string): number {
  return SCALE[type] ?? DEFAULT_SCALE;
}

/**
 * 그림이 100×100 캔버스의 **가로 중앙(50)에서 치우친 양**(스프라이트 px, 오른쪽이 +).
 * Idle/Walk 프레임의 불투명 픽셀 범위를 재서 넣었다 — 팩 그림이 캔버스 정중앙에
 * 그려져 있지 않다.
 *
 * 이걸 보정하지 않으면 **그림과 판정이 어긋나 보인다.** 판정(부채꼴·원형 광역·피격
 * 반경)은 전부 몬스터의 논리 좌표를 중심으로 도는데, 그림만 옆으로 밀려 있으면
 * "원 한가운데 서 있지 않다"로 읽힌다 — 화염 골렘의 전방향 찍기(반경 111)에서
 * 제보받은 증상이다.
 *
 * 판정은 건드리지 않는다. 논리 좌표가 맞고 그림이 틀렸으므로 그림을 옮긴다.
 */
const ART_CENTER_X: Record<string, number> = {
  demon: 3.1,
  hellhound: 0.5,
  blood: 2,
  eyeball: 0,
  lava_slime: -2,
  minotaur: 6,
  boss_demon: 5,
  boss_knight: 1.5,
  boss_golem: 2,
  boss_dark_knight: 5.5,
};

/**
 * 그림을 논리 좌표 위로 되돌리기 위해 몸통에 줄 로컬 x(월드 px).
 * @param facingLeft 좌우 반전 중이면 치우침도 같이 뒤집힌다(반전은 원점을 축으로 돈다).
 */
export function monsterArtOffsetX(type: string, facingLeft = false): number {
  const offset = -(ART_CENTER_X[type] ?? 0) * monsterScale(type);
  return facingLeft ? -offset : offset;
}

/**
 * 팩의 태그 이름(대문자 시작)을 우리가 쓰는 상태 이름에 붙인 것.
 *
 * 공격은 팩마다 Attack01~03이 있는데 첫 번째만 쓴다 — 서버가 구분하는 건 "공격했다"
 * 하나뿐이라, 어느 변형을 쓸지 고를 근거가 없다. 나중에 패턴이 갈리면 그때 늘린다.
 */
const TAG = {
  idle: 'Idle',
  walk: 'Walk',
  attack: 'Attack01',
  attack2: 'Attack02',
  attack3: 'Attack03',
  hurt: 'Hurt',
  death: 'Death',
} as const;
export type MonsterAnim = keyof typeof TAG;

/** 서버가 보내는 동작 번호(1~3) → 애니메이션 이름. 범위를 벗어나면 첫 번째 공격으로. */
const ATTACK_BY_INDEX: readonly MonsterAnim[] = ['attack', 'attack2', 'attack3'];
export function monsterAttackAnim(index: number): MonsterAnim {
  return ATTACK_BY_INDEX[index - 1] ?? 'attack';
}

/** 피격은 빠르게 지나가야 한다 — 4프레임을 18fps로 돌리면 0.22초로, 연사에도 안 밀린다. */
/**
 * 재생 속도(fps).
 *
 * 공격 동작은 **잡몹 14 / 보스 9**로 나눈다. 보스 검술의 판정 시점(monsters.json의
 * atSeconds)은 원래 프레임 번호 ÷ 14로 잡았는데, 그러면 동작이 프레임 속도 그대로
 * 튀어나와 너무 빠르다 — 예고를 보고 반응할 틈이 없다. 판정 시점을 늘리면서
 * 재생 속도도 같은 비율로 늦춰야 "칼이 뻗는 순간에 맞는다"가 유지된다.
 */
const BOSS_ATTACK_FRAME_RATE = 9;
const FRAME_RATE: Record<MonsterAnim, number> = {
  idle: 6,
  walk: 10,
  attack: 14,
  attack2: 14,
  attack3: 14,
  hurt: 18,
  death: 12,
};

/**
 * 이 타입의 이 동작을 몇 fps로 재생할지.
 *
 * 순서: 데이터의 동작별 지정(animFrameRate) → 보스 공격 기본 → 동작 기본.
 * 데이터가 이기는 이유는, 타격 시점(atSeconds)이 재생속도에서 나온 값이라 둘을
 * 같은 곳에서 정해야 어긋나지 않기 때문이다.
 */
function frameRateFor(type: string, anim: MonsterAnim): number {
  const attackIndex = ATTACK_BY_INDEX.indexOf(anim);
  if (attackIndex >= 0) {
    const override = monstersData[type]?.meleeAttacks?.find(
      (attack) => attack.anim === attackIndex + 1,
    )?.animFrameRate;
    if (override !== undefined) return override;
    if (type.startsWith('boss_')) return BOSS_ATTACK_FRAME_RATE;
  }
  return FRAME_RATE[anim];
}

/** 한 번만 재생하고 멈추는 상태(반복하면 안 되는 것들). */
const ONE_SHOT: ReadonlySet<MonsterAnim> = new Set<MonsterAnim>([
  'attack',
  'attack2',
  'attack3',
  'hurt',
  'death',
]);

/** 아틀라스 로드를 예약한다. 파일이 없으면(에셋 미보유) 조용히 넘어간다. */
export function queueMonsterAtlas(scene: Phaser.Scene): void {
  scene.load.atlas(
    MONSTER_ATLAS,
    resolveAssetUrl('assets/atlas/monsters.png'),
    resolveAssetUrl('assets/atlas/monsters.json'),
  );
}

export function hasMonsterAtlas(scene: Phaser.Scene): boolean {
  return scene.textures.exists(MONSTER_ATLAS);
}

/** 이 타입의 그림이 아틀라스에 실제로 들어 있는지(팩 일부만 있는 상태 대비). */
export function hasMonsterSprite(scene: Phaser.Scene, type: string): boolean {
  const file = SPRITE_FILE[type];
  if (!file || !hasMonsterAtlas(scene)) return false;
  return scene.textures.get(MONSTER_ATLAS).has(`${file}_${TAG.idle}_0`);
}

export function monsterAnimKey(type: string, anim: MonsterAnim): string {
  return `mob_${type}_${anim}`;
}

export function monsterIdleFrame(type: string): string {
  return `${SPRITE_FILE[type]}_${TAG.idle}_0`;
}

/** 화면에 그려지는 높이(px) = 실측 그림 높이 × 배율. HP 바를 머리 위에 띄우는 데 쓴다. */
export function monsterSpriteHeight(type: string): number {
  return (SPRITE_HEIGHT[type] ?? DEFAULT_SPRITE_HEIGHT) * monsterScale(type);
}

/**
 * 프레임 이름을 끝의 번호로 정렬해 모은다. `generateFrameNames`로 start/end를 주려면
 * 태그별 프레임 수를 미리 알아야 하는데, 그 숫자는 팩마다 다르고(같은 Walk도 6장/8장)
 * 우리가 만든 게 아니라서 코드에 박아둘 수 없다 — 아틀라스에 실제로 있는 것을 읽는다.
 */
function framesFor(scene: Phaser.Scene, file: string, tag: string): string[] {
  const prefix = `${file}_${tag}_`;
  return scene.textures
    .get(MONSTER_ATLAS)
    .getFrameNames()
    .filter((name) => name.startsWith(prefix))
    .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)));
}

/**
 * 타입 × 상태 애니메이션을 등록한다. 아틀라스에 없는 타입/태그는 건너뛴다 —
 * 없는 프레임으로 애니메이션을 만들면 재생 시 깨진다.
 */
export function registerMonsterAnimations(scene: Phaser.Scene): void {
  if (!hasMonsterAtlas(scene)) return;

  for (const [type, file] of Object.entries(SPRITE_FILE)) {
    for (const anim of Object.keys(TAG) as MonsterAnim[]) {
      const key = monsterAnimKey(type, anim);
      if (scene.anims.exists(key)) continue;

      const frames = framesFor(scene, file, TAG[anim]);
      if (frames.length === 0) continue;

      scene.anims.create({
        key,
        frames: frames.map((frame) => ({ key: MONSTER_ATLAS, frame })),
        frameRate: frameRateFor(type, anim),
        // 공격·피격·죽음은 한 번만 재생한다. 죽음은 마지막 장에서 멈춰 시체가 남고,
        // 나머지는 끝나는 즉시 렌더러가 이동/대기 상태로 되돌린다.
        repeat: ONE_SHOT.has(anim) ? 0 : -1,
      });
    }
  }
}
